/* eslint-disable camelcase */
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import DateFnsUtils from '@date-io/date-fns';
import Modal from '@material-ui/core/Modal';
import Fade from '@material-ui/core/Fade';
import Backdrop from '@material-ui/core/Backdrop';
import TextField from '@material-ui/core/TextField';
import { toast } from 'react-toastify';
import Button from '@material-ui/core/Button';
import Autocomplete from '@material-ui/lab/Autocomplete';
import PropTypes from 'prop-types';

import format from 'date-fns/format';
import { Formik, Field } from 'formik';
import { MuiPickersUtilsProvider, DatePicker } from '@material-ui/pickers';

import { createPatient } from '../store/actions/patients';
import { getAdmin, getGroups } from '../store/actions/admin';

const useStyles = makeStyles((theme) => ({
  title: {
    fontSize: 18,
    margin: 0,
    color: '#1e3f9e',
  },
  subtitle: {
    margin: 0,
    fontWeight: 'normal',
    color: '#888',
    fontSize: 12,
  },
  modal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  paper: {
    padding: '2rem',
    paddingTop: '1rem',
    minWidth: 550,
    maxWidth: '700px',
    backgroundColor: theme.palette.background.paper,
    borderRadius: 4,
    boxShadow: theme.shadows[5],
    outline: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
  },
  dropdown: {
    width: '100%',
    margin: '30px 0 10px',
    color: 'black',
  },
  horizontalRule: {
    width: '100%',
  },
  sectionHead: {
    width: '100%',
    borderBottom: 'solid 2px gray',
    color: 'gray',
  },
  noneAvailable: {
    width: '100%',
    margin: '30px 0 10px',
    color: 'black',
    textAlign: 'center',
  },
  newButton: {
    color: '#888',
    textDecoration: 'underline',
    textTransform: 'capitalize',
    alignSelf: 'flex-start',
  },
  buttonRow: {
    display: 'flex',
    flexDirection: 'row',
    marginTop: '2rem',
    width: '100%',
    justifyContent: 'space-around',
  },
  button: {
    width: '30%',
    padding: '0.6rem',
    color: 'white',
    backgroundColor: '#00d1b2',
    '&:hover': {
      backgroundColor: '#00bca0',
    },
  },
  header: {
    width: '60%',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignContent: 'center',
    marginBottom: '1rem',
  },
  form: {
    margin: '1rem',
    width: '100%',
    display: 'flex',
    flexFlow: 'row wrap',
  },
  column: {
    display: 'flex',
    flexDirection: 'column',
    width: '50%',
    alignItems: 'center',
  },
  fieldContainer: {
    width: '40%',
    marginLeft: '5%',
    marginRight: '5%',
    marginTop: '0.5rem',
    marginBottom: '0.5rem',
  },
  field: {
    width: '100%',
  },
  error: {
    color: 'red',
    marginLeft: '1rem',
  },
  nameFieldContainer: {
    width: '80%',
    marginLeft: '5%',
    marginRight: '5%',
    marginTop: '0.5rem',
    marginBottom: '0.5rem',
    flexFlow: 'column',
  },
}));

const NewPatientModal = ({ open, setOpen, setPatient }) => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const users = useSelector((state) => state.admin.usersList);
  const groups = useSelector((state) => state.admin.groupsList);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [selectedHCPs, setSelectedHCPs] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [hasNotifications, setHasNotifications] = useState(false);

  const handleClose = () => {
    setSelectedHCPs([]);
    setSelectedGroups([]);
    setOpen(false);
  };

  useEffect(() => {
    if (!users.length) dispatch(getAdmin());
    if (!groups.length) dispatch(getGroups());
  }, []);

  useEffect(() => {
    setHasNotifications(selectedGroups.length || selectedHCPs.length);
  }, [selectedGroups, selectedHCPs]);

  useEffect(() => {
    if (users.length) {
      setFilteredUsers(users.filter((user) => user.role));
    }
  }, [users]);

  const getLabel = (value) => {
    return `${value.first_name} ${value.last_name}`;
  };

  const submitHandler = (values) => {
    const body = { ...values };
    body.notify_users = selectedHCPs.map((hcp) => hcp.id);
    body.notify_groups = selectedGroups.map((group) => group.id);
    dispatch(createPatient(body));
    if (setPatient) setPatient(body);
    setOpen(false);
    toast.success('Patient created.', {
      position: 'bottom-left',
      autoClose: 3000,
      hideProgressBar: true,
      closeOnClick: true,
      pauseOnHover: true,
      theme: 'colored',
    });
  };

  return (
    <Modal
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      className={classes.modal}
      open={open}
      onClose={handleClose}
      closeAfterTransition
      BackdropComponent={Backdrop}
    >
      <Fade in={open}>
        <div className={classes.paper}>
          <h2 className={classes.title}>Create New Patient</h2>
          <Formik
            initialValues={{ first_name: '', middle_name: '', last_name: '', room: '', unit: '', mrn: '' }}
            validate={(values) => {
              const errors = {};
              if (!values.first_name) {
                errors.first_name = '*Required';
              }
              if (!values.last_name) {
                errors.last_name = '*Required';
              }
              return errors;
            }}
            onSubmit={(values, { setSubmitting }) => {
              setTimeout(() => {
                submitHandler(values);
                setSubmitting(false);
              }, 400);
            }}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              handleSubmit,
              isSubmitting,
              /* and other goodies */
            }) => {
              return (
                <MuiPickersUtilsProvider utils={DateFnsUtils}>
                  <form className={classes.form} onSubmit={handleSubmit}>
                    <div className={classes.sectionHead}>Patient Details</div>
                    <div className={classes.column}>
                      <div className={classes.nameFieldContainer}>
                        <TextField
                          className={classes.field}
                          label="First Name"
                          placeholder="Enter First Name"
                          variant="outlined"
                          name="first_name"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values.first_name}
                        />
                        {errors.first_name && touched.first_name && errors.first_name && (
                          <p className={classes.error}>
                            {errors.first_name && touched.first_name && errors.first_name}
                          </p>
                        )}
                      </div>
                      <div className={classes.nameFieldContainer}>
                        <TextField
                          className={classes.field}
                          label="Middle Name"
                          placeholder="Enter Middle Name"
                          variant="outlined"
                          name="middle_name"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values.middle_name}
                        />
                      </div>
                      <div className={classes.nameFieldContainer}>
                        <TextField
                          className={classes.field}
                          label="Last Name"
                          placeholder="Enter Last Name"
                          variant="outlined"
                          name="last_name"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values.last_name}
                        />
                        {errors.last_name && touched.last_name && errors.last_name && (
                          <p className={classes.error}>{errors.last_name && touched.last_name && errors.last_name}</p>
                        )}
                      </div>
                    </div>
                    <div className={classes.column}>
                      <div className={classes.nameFieldContainer}>
                        <TextField
                          className={classes.field}
                          label="MRN"
                          placeholder="MRN"
                          variant="outlined"
                          name="mrn"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values.mrn}
                        />
                      </div>
                    </div>
                    <div className={classes.sectionHead}>Patient Location</div>
                    <div className={classes.fieldContainer}>
                      <TextField
                        className={classes.field}
                        label="Unit"
                        placeholder="Enter Unit #"
                        variant="outlined"
                        name="unit"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.unit}
                      />
                    </div>
                    <div className={classes.fieldContainer}>
                      <TextField
                        className={classes.field}
                        label="Room"
                        placeholder="Enter Room #"
                        variant="outlined"
                        name="room"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.room}
                      />
                    </div>
                    <div className={classes.sectionHead}>Patient Notifications</div>
                    <div className={classes.column}>
                      <div className={classes.nameFieldContainer}>
                        {filteredUsers && (
                          <Autocomplete
                            multiple
                            options={filteredUsers}
                            className={classes.field}
                            getOptionLabel={(option) => getLabel(option)}
                            id="auto-complete"
                            getOptionSelected={(option, value) => option.id === value.id}
                            autoComplete
                            defaultValue={selectedHCPs}
                            onChange={(event, newValue) => setSelectedHCPs(newValue)}
                            renderInput={(params) => <TextField {...params} label="Provider" margin="normal" />}
                          />
                        )}
                      </div>
                    </div>
                    <div className={classes.column}>
                      <div className={classes.nameFieldContainer}>
                        {groups && (
                          <Autocomplete
                            multiple
                            options={groups}
                            className={classes.field}
                            getOptionLabel={(option) => option.name}
                            id="auto-complete"
                            getOptionSelected={(option, value) => option.id === value.id}
                            autoComplete
                            defaultValue={selectedGroups}
                            onChange={(event, newValue) => setSelectedGroups(newValue)}
                            renderInput={(params) => <TextField {...params} label="Group" margin="normal" />}
                          />
                        )}
                      </div>
                    </div>
                    <div className={classes.buttonRow}>
                      <Button
                        className={classes.button}
                        size="small"
                        variant="contained"
                        type="submit"
                        disabled={isSubmitting || !hasNotifications}
                      >
                        Submit
                      </Button>
                      <Button className={classes.button} size="small" variant="contained" onClick={handleClose}>
                        Cancel
                      </Button>
                    </div>
                  </form>
                </MuiPickersUtilsProvider>
              );
            }}
          </Formik>
        </div>
      </Fade>
    </Modal>
  );
};

NewPatientModal.propTypes = {
  open: PropTypes.bool.isRequired,
  setPatient: PropTypes.func,
  setOpen: PropTypes.func.isRequired,
};

NewPatientModal.defaultProps = {
  setPatient: null,
};

export default NewPatientModal;
